// The bank of the SIPP form of the client application (SEP-293).
//
// THE SOURCE IS THE FIRM'S OWN FORM: `DGW SIPP App Form (Disc/Adv/XO) v1.0
// 0725`, the form of a self-invested personal pension. The text is
// `docs/sources/sep-293-app-sipp.txt`, and the question list is
// `docs/sources/sep-293-sipp-questions.md`. The rules H1 to H17 of the design
// apply on top of the question list.
//
// # A SIPP IS THE ACCOUNT OF A PERSON
//
// The member of the SIPP fills in the form, and the account is the account
// of the member. The case is an Individual case, and the answers seed the
// fact find of a person. The member answers under the ids of the individual
// form (rule H1), with the address history of the individual form 3.0. The
// form has no nominated representative and no person group.
//
// Part 3 names the provider of the SIPP (the pension trustee). The provider
// is a firm: it is not a party of the identity check, and the fact find of a
// person has no place for it.
//
// # ONE FORM FOR EACH SERVICE
//
// Parts 4, 7 and 8 do not need to be completed for the Execution Only
// service, so each question of those parts shows only for the Discretionary
// Managed service and the Advisory service.
//
// # WHAT IS NOT HERE
//
//   - The signatures (Part 13). The submission act replaces the signature of
//     the member. The firm gets the signature of the pension trustee
//     off-line.
//   - Appendix I, the documents of a member who is not a UK national (rule
//     H14).
//   - Declaration 6c, "Schedule of Charges" (rule H8).
//
// # THE WORDING IS DRAFT
//
// Each label of the paper form is the firm's draft copy. The paper form prints
// "Neutral" as the middle point of the agreement scale. The stored answer is
// "Neither agree nor disagree", the label of the individual form, because the
// engine shows the stored label (rule H6, open question Q12).
//
// # THE IDS ARE FROZEN
//
// `FROZEN_CLIENT_APPLICATION_IDS_BY_FORM` in `client-application-frozen-ids.ts`
// pins the ids of the bank. A slice may add an id, and it may never drop or
// rename one.

import {
    addressQuestions,
    individualSectionQuestions,
    sharedServiceTypeSection,
} from './client-application-blocks';
import {
    ENTITY_BANK_RULES,
    entityAccountUsageQuestions,
    entityAdditionalNotesSection,
    entityApplicantQuestions,
    entityDeclarationQuestions,
    entityDeclarationsSection,
    entityHealthSection,
    entityPortalSecurityQuestions,
    entityThirdPartyQuestions,
} from './client-application-form-company';
import {
    PENSION_NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
    PENSION_NATIONAL_CLIENT_IDENTIFIER_LABEL,
    pensionBankAccountQuestions,
    pensionClassificationNotes,
    pensionFinancialQuestions,
    pensionObjectivesQuestions,
    pensionRiskQuestions,
    pensionSourceOfWealthQuestions,
} from './client-application-form-ssas';
import type {
    ClientApplicationBank,
    ClientApplicationSection,
    ClientApplicationStatement,
} from './client-application-questions';

/** The version of the SIPP form, from the footer of the paper form. */
export const SIPP_FORM_VERSIONS = ['1.0'] as const;

/** The description of each service, page 1 of the form. */
const SERVICE_NOTES: readonly ClientApplicationStatement[] = [
    {
        id: 'sipp_service_note_discretionary',
        text: 'Discretionary Managed service: A professional investment manager makes investment decisions for the SIPP without needing your approval each time.',
    },
    {
        id: 'sipp_service_note_advisory',
        text: 'Advisory service: You get advice from a professional investment manager, but you make the final decision on your SIPP investments and execute trades yourself.',
    },
    {
        id: 'sipp_service_note_execution_only',
        text: 'Execution only service: Dowgate Wealth is only responsible for executing the trades as requested by you. We will not offer any investment advice or recommendations.',
    },
];

/** Part 1: the member, then the employment, the marital status and the dependents. */
const PERSONAL_SECTION: ClientApplicationSection = {
    id: 'personal_information',
    part: 1,
    title: 'Main applicant - personal information',
    stepLabel: 'About you',
    intro: 'This section is to be completed by the main applicant connected to the SIPP',
    notes: SERVICE_NOTES,
    questions: [
        ...entityApplicantQuestions({
            nationalClientIdentifierExplainer: PENSION_NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
            nationalClientIdentifierLabel: PENSION_NATIONAL_CLIENT_IDENTIFIER_LABEL,
        }),
        ...individualSectionQuestions('personal_circumstances', '3.0', [
            'employment_status',
            'job_title',
            'employer_name',
            'employment_status_other',
            'marital_status',
            'has_financial_dependents',
            ...[1, 2, 3, 4].flatMap((n) => [
                `dependent_${n}_name`,
                `dependent_${n}_date_of_birth`,
                `dependent_${n}_relationship`,
                `dependent_${n}_nature`,
            ]),
        ]),
    ],
};

const PROVIDER = 'Provider details';
const CONTACT = 'SIPP provider main contact';

/** Part 3: the provider of the SIPP. */
const PROVIDER_SECTION: ClientApplicationSection = {
    id: 'sipp_provider_details',
    part: 3,
    title: 'Pension trustee details',
    stepLabel: 'Provider',
    intro: 'Please provide information about your SIPP Provider/Pension Trustees.',
    questions: [
        {
            id: 'sipp_provider_name',
            kind: 'short_text',
            label: 'Name of SIPP Provider',
            required: true,
            group: PROVIDER,
        },
        {
            id: 'sipp_account_number',
            kind: 'short_text',
            label: 'SIPP account number',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_status',
            kind: 'short_text',
            label: 'Status (Ltd, Plc, LLP etc)',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_date_of_incorporation',
            kind: 'date',
            label: 'Date of incorporation',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_country_of_incorporation',
            kind: 'country_select',
            label: 'Country of incorporation',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_lei',
            kind: 'short_text',
            label: 'Legal Entity Identifier (LEI)',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_registration_number',
            kind: 'short_text',
            label: 'Registration number',
            group: PROVIDER,
        },
        {
            id: 'sipp_provider_contact_surname',
            kind: 'short_text',
            label: 'Surname',
            group: CONTACT,
        },
        {
            id: 'sipp_provider_contact_forenames',
            kind: 'short_text',
            label: 'Forename(s)',
            group: CONTACT,
        },
        {
            id: 'sipp_provider_contact_phone',
            kind: 'short_text',
            label: 'Phone number',
            textFormat: 'phone',
            group: CONTACT,
        },
        {
            id: 'sipp_provider_contact_email',
            kind: 'short_text',
            label: 'Email',
            textFormat: 'email',
            group: CONTACT,
        },
        ...addressQuestions({
            prefix: 'sipp_provider_registered_address',
            group: 'Registered address',
            required: true,
            houseLabel: 'Building no./name',
        }),
        {
            // The paper form prints "CORRESPONDENCE ADDRESS (if different to
            // Registered Address)" and no question. The online form asks.
            id: 'sipp_provider_correspondence_address_different',
            kind: 'yes_no',
            label: 'Is the correspondence address different to the registered address?',
            required: true,
            group: 'Correspondence address',
        },
        ...addressQuestions({
            prefix: 'sipp_provider_correspondence_address',
            group: 'Correspondence address',
            required: true,
            houseLabel: 'Building no./name',
            showWhen: {
                questionId: 'sipp_provider_correspondence_address_different',
                equals: ['Yes'],
            },
        }),
    ],
};

const SIPP_DECLARATIONS: readonly ClientApplicationStatement[] = [
    {
        id: 'sipp_declaration_1',
        text: 'I have read and understood the nature and the risk of the product(s) that I intend to invest in through this account.',
    },
    {
        id: 'sipp_declaration_2',
        text: 'I act in my name as specified on this application form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are my own funds.',
    },
    {
        id: 'sipp_declaration_3',
        text: 'I am not aware of any health or other conditions that I/we may have that might affect the proper operation of the account.',
    },
    {
        id: 'sipp_declaration_4',
        text: 'I have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
    },
    {
        id: 'sipp_declaration_5',
        text: 'I accept that in certain circumstances Dowgate Wealth will be obliged to share information with UK tax authorities, who may pass it on to other tax authorities.',
    },
    { id: 'sipp_declaration_6', text: 'I have received, read and understood the following:' },
    {
        id: 'sipp_declaration_6a',
        text: 'a. Dowgate Wealth Terms of Business - including the product risks disclosure (website link: https://dowgatewealth.co.uk/terms-of-business/)',
    },
    {
        id: 'sipp_declaration_6b',
        text: 'b. Conflict of Interest Policy (website link: https://dowgatewealth.co.uk/conflicts-of-interest-policy/)',
    },
    // Rule H8: "c. Schedule of Charges" is removed on purpose, as on the
    // individual form (SEP-263.39). The lettering stays a, b, d.
    {
        id: 'sipp_declaration_6d',
        text: 'd. I have obtained the Dowgate Wealth Order Execution Policy (website link: https://dowgatewealth.co.uk/dowgate-wealth-ltd-order-execution-policy/)',
    },
    {
        id: 'sipp_declaration_7',
        text: 'I consent to the Order Execution Policy and for any orders to be executed outside a regulated market or a multilateral trading facility.',
    },
    {
        id: 'sipp_declaration_8',
        text: 'I consent for any unexecuted limit orders not to be made public.',
    },
    {
        id: 'sipp_declaration_9',
        text: 'I accept and agree to be bound by the terms provided above and consent to such terms and information including future updates to these be provided to me by way of posting on the websites indicated above.',
    },
];

/** The bank of the SIPP form, `DGW SIPP App Form (Disc/Adv/XO) v1.0`. */
export const SIPP_BANK: ClientApplicationBank = {
    versions: SIPP_FORM_VERSIONS,
    sections: [
        sharedServiceTypeSection(),
        PERSONAL_SECTION,
        // The paper form prints no intro for Part 2.
        entityHealthSection(2),
        PROVIDER_SECTION,
        {
            id: 'financial_situation',
            part: 4,
            title: 'Financial situation',
            stepLabel: 'Finances',
            intro: 'This section does not need to be completed if you have chosen an Execution Only service.',
            questions: pensionFinancialQuestions(),
        },
        {
            id: 'source_of_wealth_and_funds',
            part: 5,
            title: 'Source of wealth & funds',
            stepLabel: 'Wealth & funds',
            intro: 'In very simple terms, source of wealth is about how you earned your wealth over time, while source of funds is about where the money you’ll be using to invest comes from. We need to understand both of these elements for a clearer picture of your financial situation, so we can manage your investments and service your needs in the best way. These requirements are also part of the UK’s broader anti-money laundering regulations and help ensure the integrity of the financial system.',
            questions: pensionSourceOfWealthQuestions(),
        },
        {
            id: 'account_type_and_usage',
            part: 6,
            title: 'Account type & usage',
            stepLabel: 'Account usage',
            intro: 'It’s important for us to understand your expectations regarding funding and withdrawals to and from your portfolio, so we can plan your investments accordingly. It’s also a regulatory requirement and additional protection for you if, for example, we observe any unusual activity.',
            questions: entityAccountUsageQuestions({
                regular:
                    'For example, dividends paid out monthly or quarterly to your personal bank account',
                capital: 'For example, a property purchase is planned for next year',
                funding:
                    'For example, transfer of funds from an annual bonus to your investment account',
            }),
        },
        {
            id: 'investment_objectives_and_experience',
            part: 7,
            title: 'Investment objectives & experience',
            stepLabel: 'Objectives',
            intro: 'It’s important for us to understand your objectives and any previous investment experience you have had. Not only is this a UK regulatory requirement. It also helps ensure the portfolio we build for you is suitable, and the advice and way we communicate with you is appropriate. This section does not need to be completed if you have chosen an Execution Only account.',
            notes: pensionClassificationNotes('sipp'),
            questions: pensionObjectivesQuestions(),
        },
        {
            id: 'risk_tolerance_and_capacity_for_loss',
            part: 8,
            title: 'Risk tolerance & capacity for loss',
            stepLabel: 'Risk',
            intro: 'This section does not need to be completed if you have chosen an Execution Only account. Whether it’s your first investment or you’re an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions about your financial future. The questions in this section help us evaluate your tolerance and capacity for risk, ensuring we apply the appropriate risk profile to your portfolio. You can find out more in our Risk and Investment guide and Capacity for Loss guide.',
            questions: pensionRiskQuestions(),
        },
        {
            id: 'bank_account_information',
            part: 9,
            title: 'Bank account information',
            stepLabel: 'Bank account',
            intro: 'Please note that payments will only be made to and accepted from your nominated bank account detailed below and that a certified copy of a bank statement will be required. For joint accounts, a certified copy of a bank statement for one individual to the account will be required. For non-UK bank accounts, please provide IBAN, BIC / ABA numbers and the account currency.',
            questions: pensionBankAccountQuestions(),
        },
        {
            id: 'third_party_disclosure',
            part: 10,
            title: 'Third party information',
            stepLabel: 'Third party',
            intro: 'In certain situations, it is useful to have copies of certain reports and documentation relating to your account(s) sent to third parties. For example a tax advisor for assessment and tax filing purposes, or a lawyer for estate planning. If you would like Dowgate Wealth to provide information regarding the account to a third-party individual, please provide the relevant details below.',
            questions: entityThirdPartyQuestions(),
        },
        {
            id: 'online_portal_security_questions',
            part: 11,
            title: 'Online portal security questions',
            stepLabel: 'Portal security',
            intro: 'Our online portal gives you access to your investment portfolio, so you can see your holdings, the value of your investments and a history of all your transactions. For security, we ask the main account holder to provide answers to the following questions. Additional access can be provided upon request for third parties.',
            questions: entityPortalSecurityQuestions(),
        },
        entityAdditionalNotesSection(
            'If you need to add more information – for example additional third-party information requirements – please use the additional notes section at the end of this form.',
        ),
        entityDeclarationsSection(
            12,
            entityDeclarationQuestions({
                statements: SIPP_DECLARATIONS,
                explainer:
                    'The Pension Trustee must also sign. We will contact you to arrange this signature.',
                marketingExplainer:
                    'We offer company news and market commentary which is emailed to the contact email address detailed in Section 1. If you would like to receive this, please select your preferences:',
            }),
        ),
    ],
    ...ENTITY_BANK_RULES,
};
